import { AlertCircle, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, Card, CardContent, Spinner } from '@design-system';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import { useTicketSignIn } from '@client/hooks/use-ticket-sign-in';

/**
 * Landing page for the auto sign-in links sent in emails. Redemption, the
 * session chooser, and both redirects are driven by useTicketSignIn; this
 * component only renders the state it reports.
 */
export function AutoSignInLink() {
  const { state, confirmSignIn, continueInCurrentSession, switchAccount } = useTicketSignIn();

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-20 md:pt-24">
        <div className="w-full max-w-md">
          <Card className="bg-background gap-0 overflow-hidden rounded-2xl border p-0 shadow-none">
            <div className="p-1">
              <div className="bg-muted rounded-xl px-6 py-10 text-center">
                {state === 'invalidLink' ? (
                  <>
                    <div className="mb-6 flex justify-center">
                      <div className="flex size-16 items-center justify-center rounded-full bg-red-50">
                        <AlertCircle
                          className="size-9 text-red-600"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      This link isn&apos;t valid
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                      Please use the most recent link from your email, or sign in to continue.
                    </p>
                  </>
                ) : state === 'confirm' ? (
                  <>
                    <div className="mb-6 flex justify-center">
                      <div className="bg-primary/10 flex size-16 items-center justify-center rounded-full">
                        <ShieldCheck
                          className="text-primary size-9"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      Continue to your application
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                      For your security, confirm below to finish signing in. Only continue if you
                      requested this link.
                    </p>
                  </>
                ) : state === 'chooseSession' ? (
                  <>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      You&apos;re already signed in
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                      This link was sent to a different account. Would you like to stay signed in
                      here, or switch to the account the link was sent to?
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mb-6 flex justify-center">
                      <div className="bg-primary/10 flex size-16 items-center justify-center rounded-full">
                        <Spinner className="text-primary size-8" />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      {state === 'switchingAccount'
                        ? 'Switching accounts…'
                        : state === 'deciding'
                          ? 'One moment…'
                          : 'Signing you in…'}
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                      This will only take a moment.
                    </p>
                  </>
                )}
              </div>
            </div>

            {state === 'confirm' && (
              <CardContent className="p-6">
                <Button
                  size="lg"
                  className="w-full"
                  onClick={confirmSignIn}
                >
                  Continue securely
                </Button>
              </CardContent>
            )}

            {state === 'chooseSession' && (
              <CardContent className="flex flex-col gap-3 p-6">
                <Button
                  size="lg"
                  className="w-full"
                  onClick={switchAccount}
                >
                  Switch accounts
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full"
                  onClick={continueInCurrentSession}
                >
                  Stay signed in here
                </Button>
              </CardContent>
            )}

            {state === 'invalidLink' && (
              <CardContent className="p-6">
                <Button
                  asChild
                  size="lg"
                  className="w-full"
                >
                  <Link to="/">Go to sign in</Link>
                </Button>
              </CardContent>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
